import styled from "@emotion/styled";
import { colors } from "@ui/style/colors";
import { getAdjustedCircumferencePercentage, getCircumference } from "@utils/maths-utils";
import React, { useMemo } from "react";

interface ProgressCircleProps {
	progress: number;
	size: number;
	strokeWidth: number;
	strokeBackgroundColor?: string;
	strokeProgressColor?: string;
	counterClockWise?: boolean;
	showRemaining?: boolean;
	animated?: boolean;
	children?: React.ReactNode;
}

export const ProgressCircle: React.FC<ProgressCircleProps> = ({
	progress,
	size,
	strokeWidth,
	strokeBackgroundColor = colors.grey10,
	strokeProgressColor = colors.primary,
	counterClockWise = false,
	showRemaining = false,
	animated = false,
	children,
}) => {
	const { radius, circumference, percentage } = useMemo(() => {
		const r = (size - strokeWidth) / 2;
		const c = getCircumference(r);
		const p = getAdjustedCircumferencePercentage(c, progress, showRemaining);
		return { radius: r, circumference: c, percentage: p };
	}, [size, strokeWidth, progress, showRemaining]);

	return (
		<Container size={size}>
			<CircleContainer width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
				<circle
					r={radius}
					cx={size / 2}
					cy={size / 2}
					stroke={strokeBackgroundColor}
					strokeWidth={strokeWidth}
					strokeDasharray={circumference}
					strokeDashoffset={0}
					fill="transparent"
				/>
				<InnerCircle
					r={radius}
					cx={size / 2}
					cy={size / 2}
					stroke={strokeProgressColor}
					strokeWidth={strokeWidth}
					circumference={circumference}
					percentage={percentage}
					counterClockWise={counterClockWise}
					animated={animated}
					fill="transparent"
				/>
			</CircleContainer>
			<ChildContainer>{children}</ChildContainer>
		</Container>
	);
};

const Container = styled.div<{ size: number }>`
	position: relative;
	height: ${({ size }) => size}px;
	width: ${({ size }) => size}px;
`;

const CircleContainer = styled.svg`
	transform: rotate(-90deg);
`;

const InnerCircle = styled.circle<{
	percentage: number;
	circumference: number;
	counterClockWise: boolean;
	animated: boolean;
}>`
	stroke-linecap: round;
	stroke-dasharray: ${({ circumference }) => circumference}px;
	stroke-dashoffset: ${({ percentage }) => percentage}px;
	transform: ${({ counterClockWise }) => (counterClockWise ? "translate(0, 100%) scaleY(-1)" : "")};
	animation: ${({ animated }) => (animated ? "progress 1s ease-in-out" : "none")};

	@keyframes progress {
		from {
			stroke-dashoffset: 0;
		}
		to {
			stroke-dashoffset: ${({ percentage }) => percentage}px;
		}
	}
`;

const ChildContainer = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`;
